/**
 * Dental State Validation Engine
 * Provides comprehensive validation, guidance messages, and smart state transitions
 * for the dental chart system.
 */

import { DENTAL_STATES_CONFIG, type ToothData } from '../types/records';

// ── Surface Normalization (#49) ──
// Anterior teeth use "labial", posteriors use "buccal" — clinically same surface.
// Similarly "palatal" (upper) = "lingual" (lower).
const SURFACE_ALIASES: Record<string, string> = {
  labial: 'buccal',   // normalize labial → buccal
  palatal: 'lingual',  // normalize palatal → lingual
};

/**
 * Normalize a surface name so labial↔buccal and palatal↔lingual are unified.
 * Returns the canonical form for comparison/merging.
 */
export function normalizeSurface(surface: string): string {
  const s = surface.toLowerCase().trim();
  return SURFACE_ALIASES[s] ?? s;
}

/**
 * Normalize an array of surface names (deduplication after aliasing).
 */
export function normalizeSurfaces(surfaces: string[]): string[] {
  return [...new Set(surfaces.map(normalizeSurface))];
}

// ── Types ──

export interface ValidationResult {
  valid: boolean;
  /** Short error message (shown as toast or inline) */
  error?: string;
  /** Guidance for the dentist — what to do instead */
  guidance?: string;
  /** Non-blocking warnings (list) */
  warnings: string[];
  /** Automatic layer removals / state changes to apply before the new state */
  autoActions: AutoAction[];
}

export interface AutoAction {
  type: 'remove-layer' | 'change-base-state';
  toothNum: number;
  layerIndex?: number;
  newState?: string;
  reason: string;
}

/** Per-tooth pre-validation info used to show UI hints on state buttons */
export interface StateHint {
  stateCode: string;
  canApply: boolean;
  reason?: string;
  /** 'blocked' = cannot apply, 'warning' = can apply with warning, 'ok' = fine */
  level: 'blocked' | 'warning' | 'ok';
}

// ── Compatibility Rules ──

/**
 * States that CANNOT be applied when the tooth has a given base_state.
 * Key = current base_state, Value = array of blocked state codes.
 */
const INCOMPATIBLE_WITH_BASE: Record<string, { blocked: string[]; reason: string }> = {
  MISSING: {
    blocked: [
      'CARIES', 'AMALGAM', 'COMPOSITE', 'FRACTURE', 'SEALANT', 'CROWN', 'VENEER',
      'TEMP_FILL', 'RCT', 'ABSCESS', 'POST', 'APICOECTOMY', 'MOBILE', 'CALCULUS',
      'RECESSION', 'CERVICAL_CARIES', 'EXTRACT', 'ROOT_ONLY', 'MISSING',
    ],
    reason: 'La dent est absente',
  },
  EXTRACT: {
    blocked: [
      'CARIES', 'AMALGAM', 'COMPOSITE', 'SEALANT', 'CROWN', 'VENEER',
      'TEMP_FILL', 'CALCULUS', 'RECESSION', 'CERVICAL_CARIES', 'EXTRACT',
    ],
    reason: 'La dent est déjà marquée pour extraction',
  },
  IMPACTED: {
    blocked: [
      'CARIES', 'AMALGAM', 'COMPOSITE', 'SEALANT', 'CROWN', 'VENEER',
      'TEMP_FILL', 'CALCULUS', 'RECESSION', 'IMPACTED',
    ],
    reason: 'La dent est incluse — pas d\'accès à la couronne ou au collet',
  },
  ROOT_ONLY: {
    blocked: [
      'CARIES', 'AMALGAM', 'COMPOSITE', 'SEALANT', 'CROWN', 'VENEER',
      'TEMP_FILL', 'FRACTURE', 'ROOT_ONLY',
    ],
    reason: 'Seule la racine reste — pas de couronne pour ce traitement',
  },
};

/**
 * Guidance messages: what the dentist should do when a state is blocked.
 */
const BLOCKED_GUIDANCE: Record<string, string> = {
  // On MISSING tooth
  'MISSING→CARIES': 'Cette dent est absente. Sélectionnez "Implant" ou "Pontique" si nécessaire.',
  'MISSING→CROWN': 'Impossible de poser une couronne sur une dent absente. Envisagez un implant + couronne.',
  'MISSING→EXTRACT': 'La dent est déjà absente, pas besoin d\'extraction.',
  'MISSING→RCT': 'Pas de traitement canalaire possible sur une dent absente.',
  // On EXTRACT tooth
  'EXTRACT→CARIES': 'Cette dent est déjà marquée pour extraction. Procédez à l\'extraction d\'abord.',
  'EXTRACT→CROWN': 'Extrayez d\'abord la dent, puis envisagez un implant + couronne.',
  // On IMPACTED tooth
  'IMPACTED→CARIES': 'Dent incluse — extraction chirurgicale recommandée avant tout traitement.',
  'IMPACTED→CROWN': 'Impossible de couronner une dent incluse. Extraction chirurgicale nécessaire.',
  // On ROOT_ONLY
  'ROOT_ONLY→CARIES': 'Pas de couronne restante pour une carie. Évaluez l\'extraction ou un traitement radiculaire.',
  'ROOT_ONLY→CROWN': 'Évaluez d\'abord si la racine peut supporter un tenon + couronne.',
};

/**
 * States that auto-remove conflicting layers when applied.
 * Key = new state being applied, Value = layer codes to auto-remove.
 */
const AUTO_REMOVES: Record<string, string[]> = {
  // Extraction clears pathology (you're removing the tooth)
  EXTRACT: ['CARIES', 'TEMP_FILL', 'FRACTURE', 'CERVICAL_CARIES'],
  // MISSING clears everything (tooth is gone)
  MISSING: [
    'CARIES', 'AMALGAM', 'COMPOSITE', 'FRACTURE', 'SEALANT', 'CROWN', 'VENEER',
    'TEMP_FILL', 'RCT', 'ABSCESS', 'POST', 'APICOECTOMY', 'CALCULUS', 'RECESSION',
    'CERVICAL_CARIES',
  ],
  // Restorations replace caries on same surfaces
  AMALGAM: ['CARIES'],
  COMPOSITE: ['CARIES'],
  // Crown covers surface pathology underneath
  CROWN: ['FRACTURE', 'CARIES', 'TEMP_FILL', 'VENEER'],
  // Veneer covers front surface issues — also auto-removes conflicting CROWN (#58)
  VENEER: ['FRACTURE', 'CROWN'],
  // Post resolves abscess
  POST: ['ABSCESS'],
  // Implant requires extraction first — auto-remove existing pathology (#59)
  IMPLANT: ['CARIES', 'FRACTURE', 'TEMP_FILL', 'CERVICAL_CARIES', 'CROWN', 'VENEER', 'SEALANT', 'CALCULUS', 'RECESSION'],
  // Sealant replaces nothing (preventive)
  // PRESENT uses destructive flag — no need for redundant auto-remove list (#61)
};

/**
 * Layers that are mutually exclusive (applying one should warn/prevent the other).
 */
const LAYER_CONFLICTS: Record<string, { conflicts: string[]; message: string }> = {
  AMALGAM: {
    conflicts: ['COMPOSITE'],
    message: 'Un composite est déjà appliqué sur ces surfaces. Il sera remplacé par l\'amalgame.',
  },
  COMPOSITE: {
    conflicts: ['AMALGAM'],
    message: 'Un amalgame est déjà appliqué sur ces surfaces. Il sera remplacé par le composite.',
  },
  CROWN: {
    conflicts: ['VENEER'],
    message: 'Une facette est déjà appliquée. La couronne la remplacera.',
  },
  VENEER: {
    conflicts: ['CROWN'],
    message: 'Une couronne est déjà appliquée. La facette la remplacera.',
  },
};

// ── Bridge Validation ──

function validateBridgeSelection(teeth: number[]): ValidationResult {
  const result: ValidationResult = { valid: true, warnings: [], autoActions: [] };

  if (teeth.length < 2) {
    return {
      valid: false,
      error: 'Un bridge nécessite au minimum 2 dents sélectionnées (piliers)',
      guidance: 'Sélectionnez au moins 2 dents pour poser un bridge. Les dents intermédiaires seront automatiquement ajoutées.',
      warnings: [],
      autoActions: [],
    };
  }

  // Check if all teeth are on the same jaw
  const upper = teeth.filter(t => t >= 11 && t <= 28);
  const lower = teeth.filter(t => t >= 31 && t <= 48);

  if (upper.length > 0 && lower.length > 0) {
    return {
      valid: false,
      error: 'Bridge impossible entre mâchoires différentes',
      guidance: 'Un bridge ne peut relier que des dents de la même mâchoire. Sélectionnez des dents uniquement en haut OU en bas.',
      warnings: [],
      autoActions: [],
    };
  }

  // Check if teeth are in the same quadrant side or adjacent quadrants (crossing midline is OK for anterior bridges)
  const sorted = [...teeth].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];

  // Check for excessively long bridges (> 6 teeth span)
  const span = upper.length > 0
    ? computeSpan(min, max, true)
    : computeSpan(min, max, false);

  if (span > 6) {
    result.warnings.push(`Bridge de ${span} dents — vérifiez la faisabilité clinique d\'un bridge aussi long.`);
  }

  return result;
}

function computeSpan(minFDI: number, maxFDI: number, isUpper: boolean): number {
  // Use proper FDI sequence to avoid counting non-existent teeth like 19, 20
  const sequence = isUpper ? FDI_UPPER_SEQUENCE : FDI_LOWER_SEQUENCE;
  const minIdx = sequence.indexOf(minFDI);
  const maxIdx = sequence.indexOf(maxFDI);
  if (minIdx < 0 || maxIdx < 0) return 0;
  return Math.abs(maxIdx - minIdx) + 1;
}

// ── FDI tooth number sequences (proper dental numbering) ──
const FDI_UPPER_SEQUENCE = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const FDI_LOWER_SEQUENCE = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];
const VALID_FDI_NUMBERS = new Set([...FDI_UPPER_SEQUENCE, ...FDI_LOWER_SEQUENCE]);

// ── Duplicate Detection ──

/**
 * Check if the state is already fully applied (no new surfaces to add).
 * Returns true only if ALL requested surfaces already exist in an existing
 * layer with the same code — i.e. merging would add nothing new.
 * If the new surfaces are different/additive, returns false so the
 * apply logic can MERGE them into the existing layer.
 */
function hasDuplicateLayer(
  tooth: ToothData,
  stateCode: string,
  surfaces: string[],
): boolean {
  if (!tooth.layers) return false;

  // For WHOLE_TOOTH states, check base_state
  const config = DENTAL_STATES_CONFIG[stateCode];
  if (config?.targetZones[0] === 'WHOLE_TOOTH') {
    return tooth.base_state === stateCode;
  }

  // For layer states, check if the exact same code exists
  const existingLayer = tooth.layers.find(l => l.code === stateCode);
  if (!existingLayer) return false;

  // If no new surfaces specified AND no surfaces on existing → true duplicate
  if (surfaces.length === 0 && existingLayer.surfaces.length === 0) return true;

  // If new surfaces specified, only duplicate if ALL are already covered
  if (surfaces.length > 0) {
    const existingSurfaces = new Set(existingLayer.surfaces);
    const allCovered = surfaces.every(s => existingSurfaces.has(s));
    return allCovered; // true = nothing new to add → block; false = new surfaces → allow merge
  }

  // No new surfaces but existing has surfaces → allow (will reset surfaces)
  return false;
}

// ── Main Validation Function ──

/**
 * Validate whether a state can be applied to the given teeth.
 *
 * @param stateCode - The state to apply (e.g., 'CARIES', 'BRIDGE', 'CROWN')
 * @param teeth - Array of FDI tooth numbers to apply to
 * @param teethData - Current teeth data from the chart
 * @param surfaces - Surfaces selected for the operation (per tooth)
 * @returns ValidationResult with validity, errors, warnings, and auto-actions
 */
export function validateStateApplication(
  stateCode: string,
  teeth: number[],
  teethData: Record<string, ToothData>,
  surfacesPerTooth: Record<string, string[]>,
): ValidationResult {
  const result: ValidationResult = { valid: true, warnings: [], autoActions: [] };

  if (teeth.length === 0) {
    return {
      valid: false,
      error: 'Aucune dent sélectionnée',
      guidance: 'Cliquez sur une dent dans le schéma pour la sélectionner.',
      warnings: [],
      autoActions: [],
    };
  }

  const stateConfig = DENTAL_STATES_CONFIG[stateCode];
  if (!stateConfig) {
    return {
      valid: false,
      error: `État inconnu: ${stateCode}`,
      warnings: [],
      autoActions: [],
    };
  }

  // ── Bridge-specific validation ──
  if (stateCode === 'BRIDGE') {
    const bridgeResult = validateBridgeSelection(teeth);
    if (!bridgeResult.valid) return bridgeResult;
    result.warnings.push(...bridgeResult.warnings);

    // #11: Warn about pontic teeth — intermediate teeth should ideally be MISSING
    const bridgeTeeth = computeBridgeTeeth(teeth);
    const ponticTeeth = bridgeTeeth.filter(t => !teeth.includes(t));
    for (const pont of ponticTeeth) {
      const pontTooth: ToothData = teethData[String(pont)] ?? { base_state: 'PRESENT', layers: [] };
      if (pontTooth.base_state === 'PRESENT') {
        result.warnings.push(
          `Dent ${pont}: est un pontique intermédiaire mais n'est pas marquée comme absente. ` +
          `Vérifiez si cette dent est réellement absente.`
        );
      }
    }
  }

  // ── Per-tooth validation ──
  const errors: string[] = [];

  for (const toothNum of teeth) {
    const toothKey = String(toothNum);
    const tooth: ToothData = teethData[toothKey] ?? { base_state: 'PRESENT', layers: [] };
    const surfaces = surfacesPerTooth[toothKey] ?? [];

    // 1. Check base_state incompatibility
    const compat = INCOMPATIBLE_WITH_BASE[tooth.base_state];
    if (compat && compat.blocked.includes(stateCode)) {
      const guidanceKey = `${tooth.base_state}→${stateCode}`;
      const guidance = BLOCKED_GUIDANCE[guidanceKey];
      errors.push(`Dent ${toothNum}: ${compat.reason}`);
      if (guidance) {
        result.guidance = guidance;
      }
    }

    // 2. Duplicate detection
    if (hasDuplicateLayer(tooth, stateCode, surfaces)) {
      const surfLabel = surfaces.length > 0 ? ` (${surfaces.join(', ')})` : '';
      errors.push(`Dent ${toothNum}: "${stateConfig.label_fr}" déjà appliqué${surfLabel}`);
      if (!result.guidance) {
        result.guidance = 'Cet état est déjà présent. Retirez-le d\'abord si vous souhaitez le réappliquer.';
      }
    }

    // 3. Layer conflict detection + auto-removal preparation
    const autoRemoveCodes = AUTO_REMOVES[stateCode];
    if (autoRemoveCodes && tooth.layers) {
      for (let i = tooth.layers.length - 1; i >= 0; i--) {
        const layer = tooth.layers[i];
        if (autoRemoveCodes.includes(layer.code)) {
          // For surface-specific states, only auto-remove if surfaces overlap
          if (surfaces.length > 0 && layer.surfaces.length > 0) {
            const overlap = surfaces.some(s => layer.surfaces.includes(s));
            if (!overlap) continue;
          }
          const layerConfig = DENTAL_STATES_CONFIG[layer.code];
          result.autoActions.push({
            type: 'remove-layer',
            toothNum,
            layerIndex: i,
            reason: `"${layerConfig?.label_fr ?? layer.code}" remplacé par "${stateConfig.label_fr}"`,
          });
        }
      }
    }

    // 4. Layer mutual exclusion warnings
    const conflict = LAYER_CONFLICTS[stateCode];
    if (conflict && tooth.layers) {
      const hasConflict = tooth.layers.some(l => conflict.conflicts.includes(l.code));
      if (hasConflict) {
        result.warnings.push(`Dent ${toothNum}: ${conflict.message}`);
        // Add auto-remove for conflicting layers too
        for (let i = tooth.layers.length - 1; i >= 0; i--) {
          if (conflict.conflicts.includes(tooth.layers[i].code)) {
            const conflictConfig = DENTAL_STATES_CONFIG[tooth.layers[i].code];
            // Don't add duplicate auto-actions
            if (!result.autoActions.some(a => a.toothNum === toothNum && a.layerIndex === i)) {
              result.autoActions.push({
                type: 'remove-layer',
                toothNum,
                layerIndex: i,
                reason: `"${conflictConfig?.label_fr ?? tooth.layers[i].code}" remplacé par "${stateConfig.label_fr}"`,
              });
            }
          }
        }
      }
    }

    // 5. Surface requirement validation (#20: only for states that truly need surfaces)
    const primaryZone = stateConfig.targetZones[0];
    const surfaceRequiredStates = ['CARIES', 'AMALGAM', 'COMPOSITE', 'SEALANT', 'TEMP_FILL', 'CERVICAL_CARIES'];
    if (primaryZone === 'CROWN_SURFACE' && surfaces.length === 0 && teeth.length === 1
        && surfaceRequiredStates.includes(stateCode)) {
      result.warnings.push(
        `Dent ${toothNum}: "${stateConfig.label_fr}" s'applique normalement à des surfaces spécifiques. ` +
        `Cliquez sur les surfaces (occlusal, mesial, etc.) pour plus de précision.`
      );
    }
  }

  if (errors.length > 0) {
    result.valid = false;
    result.error = errors.join(' · ');
    if (!result.guidance) {
      result.guidance = getDefaultGuidance(stateCode, teeth, teethData);
    }
  }

  return result;
}

// ── State Hints (for UI button display) ──

/**
 * For each dental state, determine if it can be applied to the current selection.
 * Returns hints that the UI uses to show blocked/warning indicators on buttons.
 */
export function getStateHints(
  teeth: number[],
  teethData: Record<string, ToothData>,
  surfacesPerTooth: Record<string, string[]>,
): Record<string, StateHint> {
  const hints: Record<string, StateHint> = {};

  for (const [code] of Object.entries(DENTAL_STATES_CONFIG)) {
    if (teeth.length === 0) {
      hints[code] = { stateCode: code, canApply: false, level: 'blocked', reason: 'Sélectionnez une dent' };
      continue;
    }

    // Quick validation (check base state compatibility for ALL selected teeth)
    let blocked = false;
    let blockReason = '';
    let hasWarning = false;
    let warnReason = '';

    for (const toothNum of teeth) {
      const tooth: ToothData = teethData[String(toothNum)] ?? { base_state: 'PRESENT', layers: [] };
      const surfaces = surfacesPerTooth[String(toothNum)] ?? [];

      // Check incompatibility
      const compat = INCOMPATIBLE_WITH_BASE[tooth.base_state];
      if (compat && compat.blocked.includes(code)) {
        blocked = true;
        blockReason = `Dent ${toothNum}: ${compat.reason}`;
        break;
      }

      // Check duplicate  
      if (hasDuplicateLayer(tooth, code, surfaces)) {
        blocked = true;
        blockReason = `Déjà appliqué sur #${toothNum}`;
        break;
      }

      // Check conflict warnings
      const conflict = LAYER_CONFLICTS[code];
      if (conflict && tooth.layers?.some(l => conflict.conflicts.includes(l.code))) {
        hasWarning = true;
        warnReason = conflict.message;
      }
    }

    // Bridge: check multi-tooth requirement
    if (code === 'BRIDGE' && teeth.length < 2) {
      blocked = true;
      blockReason = 'Sélectionnez au moins 2 dents pour un bridge';
    }

    if (blocked) {
      hints[code] = { stateCode: code, canApply: false, level: 'blocked', reason: blockReason };
    } else if (hasWarning) {
      hints[code] = { stateCode: code, canApply: true, level: 'warning', reason: warnReason };
    } else {
      hints[code] = { stateCode: code, canApply: true, level: 'ok' };
    }
  }

  return hints;
}

// ── Default guidance messages ──

function getDefaultGuidance(
  stateCode: string,
  teeth: number[],
  teethData: Record<string, ToothData>,
): string {
  const config = DENTAL_STATES_CONFIG[stateCode];
  if (!config) return '';

  // Check what base states the teeth have
  const baseStates = teeth.map(t => teethData[String(t)]?.base_state ?? 'PRESENT');
  const hasMissing = baseStates.includes('MISSING');
  const hasExtract = baseStates.includes('EXTRACT');

  if (hasMissing) {
    if (config.targetZones[0] === 'ROOT' || config.targetZones[0] === 'CROWN_SURFACE' || config.targetZones[0] === 'CROWN_WHOLE') {
      return 'Cette dent est absente. Vous pouvez appliquer "Implant" ou "Pontique" pour la remplacer.';
    }
  }

  if (hasExtract) {
    return 'Cette dent est marquée pour extraction. Complétez d\'abord l\'extraction, puis marquez-la comme "Absente".';
  }

  return 'Vérifiez l\'état actuel de la dent et choisissez un état compatible.';
}

// ── Utility: compute all teeth in bridge span ──

export function computeBridgeTeeth(selectedTeeth: number[]): number[] {
  if (selectedTeeth.length < 2) return selectedTeeth;

  // Check cross-jaw
  const upper = selectedTeeth.filter(t => t >= 11 && t <= 28);
  const lower = selectedTeeth.filter(t => t >= 31 && t <= 48);
  if (upper.length > 0 && lower.length > 0) {
    return selectedTeeth; // Invalid — don't expand, let validation catch it
  }

  const isUpper = upper.length > 0;
  const sequence = isUpper ? FDI_UPPER_SEQUENCE : FDI_LOWER_SEQUENCE;

  // Find the range of positions in the FDI sequence
  const positions = selectedTeeth
    .map(t => sequence.indexOf(t))
    .filter(idx => idx >= 0)
    .sort((a, b) => a - b);

  if (positions.length < 2) return selectedTeeth;

  const minPos = positions[0];
  const maxPos = positions[positions.length - 1];

  // Fill all teeth between min and max positions in the FDI sequence
  const filled: number[] = [];
  for (let i = minPos; i <= maxPos; i++) {
    filled.push(sequence[i]);
  }
  return filled;
}
/** Validate an FDI number is a real permanent tooth */
export function isValidFDI(fdi: number): boolean {
  return VALID_FDI_NUMBERS.has(fdi);
}